import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import { Book, BarChart2, FileText, Users, Award, Monitor } from "lucide-react";

export default function AdvancedExcel() {
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
    window.scrollTo(0, 0);
  }, []);

  const curriculum = [
    {
      title: "Excel Fundamentals & Shortcuts",
      icon: Book,
      desc: "Get comfortable with Excel basics, navigation, shortcuts, and essential formatting to speed up your workflow.",
    },
    {
      title: "Advanced Formulas & Functions",
      icon: FileText,
      desc: "Master formulas like VLOOKUP, INDEX-MATCH, IF statements, and dynamic array functions for complex calculations.",
    },
    {
      title: "Data Analysis & Pivot Tables",
      icon: BarChart2,
      desc: "Learn how to summarize, filter, and analyze large datasets with PivotTables and advanced Excel analysis tools.",
    },
    {
      title: "Macros & VBA Automation",
      icon: Monitor,
      desc: "Automate repetitive tasks using Macros and VBA, boosting efficiency and reducing manual errors.",
    },
    {
      title: "Data Visualization & Dashboards",
      icon: Award,
      desc: "Create interactive charts, dashboards, and visual reports to present insights clearly and professionally.",
    },
    {
      title: "Real-World Excel Projects",
      icon: Users,
      desc: "Apply your skills on real projects from finance, reporting, and business analytics for hands-on experience.",
    },
  ];

  const careers = [
    {
      title: "Data Analyst",
      desc: "Use your Excel skills to analyze business data, create reports, and provide actionable insights for decision making.",
    },
    {
      title: "Business Analyst",
      desc: "Leverage advanced Excel for business process analysis, reporting, and project evaluation to drive efficiency.",
    },
    {
      title: "Finance Executive",
      desc: "Manage financial data, generate statements, and perform analysis using Excel for accurate and timely decisions.",
    },
    {
      title: "Reporting Specialist",
      desc: "Prepare detailed reports, dashboards, and visual summaries to support business operations and management.",
    },
    {
      title: "Excel Consultant",
      desc: "Provide expert guidance and solutions in Excel automation, dashboards, and data management for clients or teams.",
    },
  ];

  const faqs = [
    {
      question: "Do I need prior Excel experience?",
      answer:
        "Basic Excel knowledge helps, but this course covers advanced topics step-by-step for all learners.",
    },
    {
      question: "Will I learn Macros & VBA?",
      answer:
        "Yes, we cover automation using Macros and VBA to save time in real projects.",
    },
    {
      question: "Are there real-world case studies?",
      answer:
        "Absolutely! You'll work on business finance, reporting, and dashboard projects.",
    },
    {
      question: "Is there placement support?",
      answer:
        "Yes, we provide guidance to help you secure roles in data analysis, finance, and reporting.",
    },
  ];

  // 🎨 Different gradient borders
  const gradientBorders = [
    "from-indigo-500 to-purple-500",
    "from-green-400 to-blue-500",
    "from-pink-500 to-orange-400",
    "from-cyan-400 to-violet-500",
    "from-yellow-400 to-red-500",
    "from-teal-400 to-indigo-500",
  ];

  return (
    <div className="fade-in-page bg-gray-50 text-gray-900 mt-5">
      {/* Custom Animations */}
      <style>{`
        @keyframes jump {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12%); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .animate-jump { animation: jump 1s infinite; }
        .animate-shake { animation: shake 0.8s infinite; }
        .animate-spin-slow { animation: spin 6s linear infinite; }
      `}</style>

      {/* Hero Section */}
      <section
        className="relative flex items-center justify-center py-24 text-center bg-cover bg-center"
        style={{
          backgroundImage:
            "url('/assets/Wallpaper_Excel_8.png')",
        }}
        data-aos="zoom-in"
      >
        <div className="relative z-10 max-w-3xl px-4">
          <h1 className="text-5xl font-extrabold mb-6 text-white drop-shadow-xl animate-pulse">
            Advanced Excel Mastery
          </h1>
          <p className="text-lg mb-6 text-white animate-fadeInUp">
            Transform raw data into actionable insights with advanced Excel
            skills, automation, and dashboards.
          </p>

          <Link
            to="/contact"
            className="inline-block px-8 py-3 bg-gradient-to-r from-green-400 to-indigo-500 text-white font-bold rounded-full shadow-xl hover:scale-110 transition-transform animate-bounce"
            data-aos="flip-up"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* Why Learn Excel */}
      <section className="py-20 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] bg-gray-50">

        <div className="container mx-auto px-6">
          <h2
            className="text-4xl font-bold text-center mb-12"
            data-aos="fade-up"
          >
            Why Learn Advanced Excel?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Boost Productivity",
                icon: Book,
                desc: "Automate repetitive tasks, save hours using advanced formulas, and increase efficiency with VBA scripts.",
                anim: "flip-left",
              },
              {
                title: "Data-Driven Decisions",
                icon: BarChart2,
                desc: "Analyze complex datasets, generate insights, and create dashboards for informed business decisions.",
                anim: "fade-up",
              },
              {
                title: "Career Growth",
                icon: Award,
                desc: "Advanced Excel skills open doors to data analytics, finance, reporting, and consulting roles.",
                anim: "flip-right",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className={`relative bg-white p-6 rounded-2xl shadow-lg border-4 border-transparent bg-gradient-to-r ${
                  gradientBorders[idx % gradientBorders.length]
                } bg-clip-border hover:scale-105 transition-all transform animate-shake`}
                data-aos={item.anim}
                data-aos-delay={idx * 200}
              >
                <div
                  className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gradient-to-r ${
                    gradientBorders[idx % gradientBorders.length]
                  } text-white animate-spin-slow shadow-md`}
                >
                  <item.icon size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-center animate-jump">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-center">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 bg-[url('https://www.transparenttextures.com/patterns/crossword.png')]">
        <div className="container mx-auto px-6">
          <h2
            className="text-4xl font-bold text-center mb-12"
            data-aos="fade-down"
          >
            Course Curriculum
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {curriculum.map((item, idx) => (
              <div
                key={idx}
                className={`flex flex-col items-center bg-white p-6 rounded-2xl shadow-md border-4 border-transparent bg-gradient-to-r ${
                  gradientBorders[idx % gradientBorders.length]
                } bg-clip-border hover:scale-105 transition-all transform animate-shake`}
                data-aos={idx % 2 === 0 ? "zoom-in-up" : "zoom-in-down"}
                data-aos-delay={idx * 120}
              >
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-r ${
                    gradientBorders[idx % gradientBorders.length]
                  } text-white mb-4 animate-spin-slow`}
                >
                  <item.icon size={28} />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-center animate-jump">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-sm text-center">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="py-20 bg-[url('https://www.transparenttextures.com/patterns/geometry.png')] bg-gray-50">
        <div className="container mx-auto px-6">
          <h2
            className="text-4xl font-bold text-center mb-12"
            data-aos="fade-up"
          >
            Career Opportunities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {careers.map((career, idx) => (
              <div
                key={idx}
                className={`bg-white p-6 rounded-2xl shadow-md border-4 border-transparent bg-gradient-to-r ${
                  gradientBorders[idx % gradientBorders.length]
                } bg-clip-border hover:scale-105 transition-all transform animate-shake`}
                data-aos={idx % 2 === 0 ? "fade-up-right" : "fade-up-left"}
                data-aos-delay={idx * 150}
              >
                <div
                  className={`w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-gradient-to-r ${
                    gradientBorders[idx % gradientBorders.length]
                  } text-white animate-spin-slow`}
                >
                  <BarChart2 size={24} />
                </div>
                <h3 className="font-semibold text-xl mb-2 text-center animate-jump">
                  {career.title}
                </h3>
                <p className="text-gray-700 text-sm text-center">
                  {career.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-[url('https://www.transparenttextures.com/patterns/light-paper-fibers.png')]">
        <div className="container mx-auto px-6">
          <h2
            className="text-4xl font-bold text-center mb-12"
            data-aos="fade-up"
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`border-4 border-transparent bg-gradient-to-r ${
                  gradientBorders[idx % gradientBorders.length]
                } bg-clip-border rounded-2xl p-6 shadow-md bg-white hover:scale-105 transition-all transform animate-shake`}
                data-aos={idx % 2 === 0 ? "fade-right" : "fade-left"}
                data-aos-delay={idx * 150}
              >
                <h3 className="font-semibold text-lg mb-2 animate-jump">
                  {faq.question}
                </h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-center">
        <h2 className="text-4xl font-bold mb-6" data-aos="zoom-in">
          Ready to Become an Excel Expert?
        </h2>
        <p className="text-lg mb-8" data-aos="fade-up">
          Join our Advanced Excel course and take your career to the next level.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-2 bg-white text-indigo-600 font-bold rounded-lg shadow-xl hover:scale-110 transition-transform animate-bounce animate-pulse text-sm"
          data-aos="flip-up"
        >
          Enroll Now
        </Link>
      </section>
    </div>
  );
}
